import type { TFunction } from "i18next";
import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import { Api } from "services/api/api.service";
import type {
  EmploymentTemplate,
  EmploymentTemplateListItem,
} from "services/api/company/staff.service";
import type { AddStaffFormData } from "./addStaffPanel.contract";
import { fromApiPhoneNumber, toApiPhoneNumber } from "utils/phoneNumber.logic";
import { mapAddStaffFormDataToWorkerPayload } from "./addStaffPanel.mapper";
import { resolveCreateWorkerErrorMessage } from "./addStaffPanel.error.logic";

export type PhoneLookupState =
  | { readonly status: "idle" }
  | { readonly status: "checking" }
  | { readonly message: string; readonly status: "invalid" }
  | { readonly message: string; readonly status: "alreadyExists" }
  | { readonly status: "verified" };

export type SubmitState =
  | { readonly status: "idle" }
  | { readonly status: "submitting" };

export type EmploymentTemplateDetails = Readonly<
  Record<string, EmploymentTemplate>
>;

interface FetchEmployeeNumberDeps {
  readonly companyId: number;
  readonly isMountedRef: MutableRefObject<boolean>;
  readonly requestId: number;
  readonly getCurrentRequestId: () => number;
  readonly setField: <K extends keyof AddStaffFormData>(
    field: K,
    value: AddStaffFormData[K],
  ) => void;
  readonly setIsLoadingEmployeeNumber: Dispatch<SetStateAction<boolean>>;
}

export async function fetchEmployeeNumberRequest(
  deps: FetchEmployeeNumberDeps,
): Promise<void> {
  try {
    const response =
      await Api().company.organizationalStructure.getEmployeeNumber(
        deps.companyId,
      );
    if (!deps.isMountedRef.current) return;
    if (deps.getCurrentRequestId() !== deps.requestId) return;
    deps.setField("employeeNumber", String(response.data ?? ""));
  } catch {
    if (!deps.isMountedRef.current) return;
    if (deps.getCurrentRequestId() !== deps.requestId) return;
  } finally {
    if (!deps.isMountedRef.current) return;
    if (deps.getCurrentRequestId() !== deps.requestId) return;
    deps.setIsLoadingEmployeeNumber(false);
  }
}

interface FetchTaxTableDeps {
  readonly companyId: number;
  readonly isMountedRef: MutableRefObject<boolean>;
  readonly personalNumber: string;
  readonly requestId: number;
  readonly getCurrentRequestId: () => number;
  readonly setField: <K extends keyof AddStaffFormData>(
    field: K,
    value: AddStaffFormData[K],
  ) => void;
  readonly setIsLoadingTaxTable: Dispatch<SetStateAction<boolean>>;
}

/**
 * Fills in the tax table registered for a personal number. The field stays
 * editable and keeps whatever it holds when the lookup finds nothing, so a
 * person Skatteverket has no answer for is not left worse off than before.
 */
export async function fetchTaxTableRequest(
  deps: FetchTaxTableDeps,
): Promise<void> {
  try {
    const response = await Api().taxTable.getTaxTableByPersonalNumber({
      companyId: deps.companyId,
      personalNumber: deps.personalNumber,
    });
    if (!deps.isMountedRef.current) return;
    if (deps.getCurrentRequestId() !== deps.requestId) return;

    const foundTaxTable = response.data?.data?.taxTable;
    if (typeof foundTaxTable !== "number" || foundTaxTable <= 0) return;
    deps.setField("taxTable", String(foundTaxTable));
  } catch {
    if (!deps.isMountedRef.current) return;
    if (deps.getCurrentRequestId() !== deps.requestId) return;
  } finally {
    if (!deps.isMountedRef.current) return;
    if (deps.getCurrentRequestId() !== deps.requestId) return;
    deps.setIsLoadingTaxTable(false);
  }
}

interface FetchEmploymentTemplatesDeps {
  readonly companyId: number;
  readonly isMountedRef: MutableRefObject<boolean>;
  readonly requestId: number;
  readonly getCurrentRequestId: () => number;
  readonly setEmploymentTemplates: Dispatch<
    SetStateAction<EmploymentTemplateListItem[]>
  >;
  readonly setIsLoadingEmploymentTemplates: Dispatch<SetStateAction<boolean>>;
}

export async function fetchEmploymentTemplatesRequest(
  deps: FetchEmploymentTemplatesDeps,
): Promise<void> {
  try {
    const response = await Api().company.staff.getEmploymentTemplates(
      deps.companyId,
    );
    if (!deps.isMountedRef.current) return;
    if (deps.getCurrentRequestId() !== deps.requestId) return;
    deps.setEmploymentTemplates(response.data ?? []);
  } catch {
    if (!deps.isMountedRef.current) return;
  } finally {
    if (!deps.isMountedRef.current) return;
    if (deps.getCurrentRequestId() !== deps.requestId) return;
    deps.setIsLoadingEmploymentTemplates(false);
  }
}

interface FetchEmploymentTemplateDetailsDeps {
  readonly companyId: number;
  readonly employmentTemplateIds: number[];
  readonly isMountedRef: MutableRefObject<boolean>;
  readonly requestId: number;
  readonly getCurrentRequestId: () => number;
  readonly setEmploymentTemplateDetails: Dispatch<
    SetStateAction<EmploymentTemplateDetails>
  >;
  readonly setIsLoadingEmploymentTemplateDetails: Dispatch<
    SetStateAction<boolean>
  >;
}

/**
 * Loads every employment template's detail in parallel. The template list
 * endpoint only returns id and name, so this is the only way to know a
 * template's salaries before it has been selected. Templates that fail to load
 * are skipped rather than failing the whole batch.
 */
export async function fetchEmploymentTemplateDetailsRequest(
  deps: FetchEmploymentTemplateDetailsDeps,
): Promise<void> {
  const results = await Promise.all(
    deps.employmentTemplateIds.map(async (employmentTemplateId) => {
      try {
        const response = await Api().company.staff.getEmploymentTemplate(
          deps.companyId,
          employmentTemplateId,
        );
        return { employmentTemplateId, template: response.data ?? null };
      } catch {
        return { employmentTemplateId, template: null };
      }
    }),
  );
  if (!deps.isMountedRef.current) return;
  if (deps.getCurrentRequestId() !== deps.requestId) return;
  deps.setEmploymentTemplateDetails((previousDetails) => {
    const nextDetails: Record<string, EmploymentTemplate> = {
      ...previousDetails,
    };
    for (const result of results) {
      if (result.template == null) continue;
      nextDetails[String(result.employmentTemplateId)] = result.template;
    }
    return nextDetails;
  });
  deps.setIsLoadingEmploymentTemplateDetails(false);
}

interface LookupWorkerByPhoneDeps {
  readonly companyId: number;
  readonly isMountedRef: MutableRefObject<boolean>;
  readonly phoneNumber: string;
  readonly requestId: number;
  readonly translate: TFunction;
  readonly getCurrentRequestId: () => number;
  readonly markAutofilledFields: (fields: Set<keyof AddStaffFormData>) => void;
  readonly setFormData: Dispatch<SetStateAction<AddStaffFormData>>;
  readonly setPhoneLookupState: Dispatch<SetStateAction<PhoneLookupState>>;
}

export async function lookupWorkerByPhoneRequest(
  deps: LookupWorkerByPhoneDeps,
): Promise<void> {
  const apiPhoneNumber = toApiPhoneNumber(deps.phoneNumber);
  if (!apiPhoneNumber.succeeded) {
    // The lookup runs from a debounce while the number is still being typed, so
    // one that does not convert yet is left alone rather than called out.
    deps.setPhoneLookupState({ status: "idle" });
    return;
  }

  try {
    const response =
      await Api().company.organizationalStructure.getWorkerByNumber(
        deps.companyId,
        apiPhoneNumber.data,
      );
    if (!deps.isMountedRef.current) return;
    if (deps.getCurrentRequestId() !== deps.requestId) return;

    const worker = response.data;
    if (!worker || worker.workerId === 0) {
      deps.setPhoneLookupState({ status: "verified" });
      return;
    }
    if (worker.employmentInfo != null) {
      deps.setPhoneLookupState({
        status: "alreadyExists",
        message: deps.translate("Staff.AddStaffPanelWorkerAlreadyExists"),
      });
      return;
    }

    deps.setPhoneLookupState({ status: "verified" });
    deps.setFormData((previousFormData) => ({
      ...previousFormData,
      firstName: worker.firstName ?? "",
      lastName: worker.lastName ?? "",
      email: worker.email ?? "",
      personalIdentityNumber: worker.personalIdentityNumber ?? "",
      phoneNumber:
        worker.phoneNumber == null
          ? previousFormData.phoneNumber
          : fromApiPhoneNumber(worker.phoneNumber),
    }));
    deps.markAutofilledFields(
      new Set(
        (
          [
            ["firstName", worker.firstName],
            ["lastName", worker.lastName],
            ["email", worker.email],
            ["personalIdentityNumber", worker.personalIdentityNumber],
          ] as const
        )
          .filter(([, value]) => (value ?? "").length > 0)
          .map(([field]) => field),
      ),
    );
  } catch {
    if (!deps.isMountedRef.current) return;
    if (deps.getCurrentRequestId() !== deps.requestId) return;
  } finally {
    if (!deps.isMountedRef.current) return;
    deps.setPhoneLookupState((previousPhoneLookupState) => {
      return previousPhoneLookupState.status === "checking"
        ? { status: "idle" }
        : previousPhoneLookupState;
    });
  }
}

interface CreateWorkerDeps {
  readonly companyId: number;
  readonly formData: AddStaffFormData;
  readonly isMountedRef: MutableRefObject<boolean>;
  readonly payload: NonNullable<
    ReturnType<typeof mapAddStaffFormDataToWorkerPayload>
  >;
  readonly requestId: number;
  readonly translate: TFunction;
  readonly getCurrentRequestId: () => number;
  readonly onClose: () => void;
  readonly onWorkerCreated?: () => void;
  readonly setSubmitError: Dispatch<SetStateAction<string>>;
  readonly setSubmitState: Dispatch<SetStateAction<SubmitState>>;
}

export async function createWorkerRequest(
  deps: CreateWorkerDeps,
): Promise<void> {
  try {
    await Api().company.staff.createWorker(deps.companyId, deps.payload);
    if (!deps.isMountedRef.current) return;
    if (deps.getCurrentRequestId() !== deps.requestId) return;
    deps.onWorkerCreated?.();
    deps.onClose();
  } catch (error) {
    if (!deps.isMountedRef.current) return;
    if (deps.getCurrentRequestId() !== deps.requestId) return;
    deps.setSubmitError(
      resolveCreateWorkerErrorMessage({
        error,
        formData: deps.formData,
        translate: deps.translate,
      }),
    );
  } finally {
    if (!deps.isMountedRef.current) return;
    if (deps.getCurrentRequestId() !== deps.requestId) return;
    deps.setSubmitState({ status: "idle" });
  }
}
